import { Combobox, TextField, ValidationMessage } from '@schuettflix/react-components';
import React, { ChangeEvent } from 'react';
import { parsePhoneNumber as parser } from 'awesome-phonenumber';
import {
    countryCodes,
    CountryCodeSchema,
    getCountryCodeByPrefix,
} from '@/modules/location/components/phoneinput/CountryCodes.ts';
import { z } from 'zod';

type CountryCodeOption = {
    isoCode: string;
    value: string;
    label: string;
};

export const missingPhoneNumberKey = 'phone.missingPhoneNumberKey';
export const missingCountryCodeKey = 'phone.missingCountryCode';
export const invalidPhoneNumberKey = 'phone.invalidPhoneNumberKey';
export const PhoneFormSchema = z
    .object({
        number: z.string().nullable(),
        countryCode: CountryCodeSchema.nullable(),
    })
    .refine(phone => phone.number && phone.number.length > 0, missingPhoneNumberKey)
    .refine(phone => phone.countryCode != null, missingCountryCodeKey)
    .refine(phone => {
        if (!phone.countryCode) return false;

        const composedNumber = (phone.countryCode.code + phone.number).trim().replace(/^00/, '+');
        return parser(composedNumber, { regionCode: phone.countryCode.isoCode }).valid;
    }, invalidPhoneNumberKey);
export type FormPhone = z.input<typeof PhoneFormSchema>;

export type PhoneInputProps = {
    value: FormPhone | null;
    onChange: (value: FormPhone | null) => void;
    countryIsoCodeFilters?: string[];
    placeholder?: string;
    label?: string;
    errorMessage?: string;
};

export const PhoneInput = ({
    value = {
        number: null,
        countryCode: null,
    },
    onChange,
    countryIsoCodeFilters = [],
    placeholder,
    label,
    errorMessage,
}: PhoneInputProps) => {
    const getCountryCodeOptions = (): CountryCodeOption[] => {
        return countryCodes
            .filter(countryCode => {
                if (countryIsoCodeFilters.length) {
                    return true;
                }

                return countryIsoCodeFilters.includes(countryCode.isoCode);
            })
            .map(countryCode => {
                return {
                    isoCode: countryCode.isoCode,
                    value: countryCode.code,
                    label: countryCode.code,
                };
            })
            .toSorted((a, b) => a.value.localeCompare(b.value));
    };

    const handleCountryCodeChange = (newCountryCode: string | null): void => {
        if (!newCountryCode)
            return onChange({
                number: value?.number ?? null,
                countryCode: null,
            });

        onChange({
            number: value?.number ?? null,
            countryCode: getCountryCodeByPrefix(newCountryCode) ?? null,
        });
    };

    const handlePhoneNumberChange = (e: ChangeEvent<HTMLInputElement>): void => {
        const filteredValue = e.target.value.replace(/[^0-9]/g, '');
        onChange({
            number: filteredValue,
            countryCode: value?.countryCode ?? null,
        });
    };

    return (
        <ValidationMessage className="relative" message={errorMessage}>
            <div className="flex">
                <Combobox
                    value={value?.countryCode?.code ?? null}
                    errorMessage={errorMessage ? '' : undefined}
                    onChange={handleCountryCodeChange}
                    options={getCountryCodeOptions()}
                    buttonClassName="rounded-r-none"
                    className="-mr-px w-[8rem] rounded-r-none focus-within:z-10 hover:z-10"
                    variant="sm"
                />
                <TextField
                    value={value?.number ?? ''}
                    onChange={handlePhoneNumberChange}
                    placeholder={placeholder}
                    isCritical={!!errorMessage}
                    label={label}
                    inputClassName="rounded-l-none"
                    className="flex-1"
                    variant="sm"
                />
            </div>
        </ValidationMessage>
    );
};
